import {
  DeleteOutline as DeleteOutlinedIcon,
  Edit,
  MoreVertRounded,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { type MouseEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'src/constants/paths';
import {
  TEMP_STORED_PRODUCT_DATA,
  TEMP_STORED_PRODUCT_FILE,
} from 'src/features/products/editProductPage/hooks/EditProductPage.helper';
import { useMenu, useModal } from 'src/hooks';
import { IProductVariation } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { Menu } from '../menu';
import { SimpleDialog } from '../modal';
import { ProductVariationIcon } from '../productVariationForm/ProductVariationForm.styled';
import { MenuIconWrapper } from './TemplateVariationMoreOptions.styled';

export const TemplateVariationMoreOptions = ({
  productVariation,
  onDeleteDyDisplay,
  onBeforeNavigate,
}: {
  productVariation: IProductVariation;
  onDeleteDyDisplay: () => void;
  onBeforeNavigate?: () => void;
}) => {
  const dyTemplateMenu = useMenu();
  const deleteConfirmDialog = useModal();
  const navigate = useNavigate();
  const formContext = useFormContext();

  const handleEdit = async () => {
    dyTemplateMenu.onCloseMenu();
    onBeforeNavigate?.();
    // Temporarily store the form values
    sessionStorage.setItem(
      TEMP_STORED_PRODUCT_DATA(productVariation.group_product_code),
      JSON.stringify(formContext.getValues()),
    );

    // Save asset separately if it's a File
    const asset = formContext.getValues('asset');
    if (asset instanceof File) {
      const dataURL = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(asset);
      });
      sessionStorage.setItem(
        TEMP_STORED_PRODUCT_FILE(productVariation.group_product_code),
        JSON.stringify({
          dataURL,
          name: asset.name,
          type: asset.type,
          size: asset.size,
          lastModified: asset.lastModified,
        }),
      );
    }

    const templateId = productVariation.variation_template?.template_id;
    if (
      productVariation.group_product_code &&
      productVariation.product_code &&
      templateId
    ) {
      navigate(
        PATHS.PRODUCT_VARIATION_TEMPLATE_EDIT(
          productVariation.group_product_code,
          productVariation.product_code,
          String(templateId),
        ),
      );
    }
  };

  const handleDelete = () => {
    dyTemplateMenu.onCloseMenu();
    deleteConfirmDialog.onOpenModal(onDeleteDyDisplay);
  };

  const { isViewOnly } = useUserRole();

  const items = [
    {
      label: lang.edit,
      onClick: handleEdit,
      leftIcon: (
        <MenuIconWrapper component="span">
          <Edit fontSize="small" />
        </MenuIconWrapper>
      ),
    },
    {
      label: lang.deleteText,
      onClick: handleDelete,
      leftIcon: (
        <MenuIconWrapper component="span">
          <DeleteOutlinedIcon />
        </MenuIconWrapper>
      ),
    },
  ];

  const onMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    dyTemplateMenu.onOpenMenu(event.currentTarget);
  };

  return (
    <>
      <Grid
        item
        xs={0.6}
        display={'flex'}
        justifyContent={'end'}
        paddingTop={2.5}>
        <ProductVariationIcon
          $marginRight={0}
          onClick={onMenuClick}
          disabled={isViewOnly}>
          <MoreVertRounded />
        </ProductVariationIcon>

        <Menu
          anchorEl={dyTemplateMenu.anchorEl}
          items={items}
          onClose={dyTemplateMenu.onCloseMenu}
        />
      </Grid>
      <SimpleDialog
        actionColor="error"
        open={deleteConfirmDialog.open}
        title={lang.deleteTemplateTitle}
        subTitle={lang.deleteTemplateSubTitle}
        confirmText={lang.deleteText}
        cancelText={lang.cancel}
        onClose={deleteConfirmDialog.onCloseModal}
        onClickCancel={deleteConfirmDialog.onCloseModal}
        onClickConfirm={deleteConfirmDialog.customFn}
        modalSize="sm"
      />
    </>
  );
};
